
import React from "react";
import { format } from "date-fns";
import { CalendarIcon, Clock, Users, UserCheck } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "./StatusBadge";
import { useIsMobile } from "@/hooks/use-mobile";
import { BookingData } from "@/types/types";

interface ClassRowProps {
  booking: BookingData;
  capacity: number;
  attendeesCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  status: 'upcoming' | 'completed' | 'cancelled';

}

export const ClassRow: React.FC<ClassRowProps> = ({
  booking,
  capacity,
  attendeesCount,
  isExpanded,
  onToggleExpand,
  status
}) => {
  // Calculate spots left
  const spotsLeft = capacity - attendeesCount;
  const isMobile = useIsMobile();
  
  return (
    <TableRow 
      className="cursor-pointer hover:bg-secondary/10" 
      onClick={onToggleExpand}
      data-testid={`booking-row-${booking.id}`}
      aria-expanded={isExpanded}
    >
      <TableCell className={`font-medium ${isMobile ? "px-1 py-1 text-[10px]" : ""}`}>
        {isMobile && booking.className.length > 12 
          ? `${booking.className.substring(0, 10)}...` 
          : booking.className}
      </TableCell>
      <TableCell className={isMobile ? "px-1 py-1 whitespace-nowrap" : ""}>
        <div className={`flex items-center gap-1 ${isMobile ? "text-[9px]" : ""}`}>
          <CalendarIcon className={`${isMobile ? "h-2.5 w-2.5" : "h-4 w-4"} text-muted-foreground`} />
          <span>{format(new Date(booking.date), "dd/MM")}</span>
        </div>
        <div className={`flex items-center gap-1 text-muted-foreground ${isMobile ? "text-[9px]" : ""}`}>
          <Clock className={`${isMobile ? "h-2.5 w-2.5" : "h-4 w-4"}`} />
          <span>{booking.time.substring(0, 5) || '00:00'} ({booking.duration}min)</span>
        </div>
      </TableCell>
      <TableCell className={isMobile ? "px-1 py-1" : ""}>
        <div className={`flex items-center gap-1 ${isMobile ? "text-[9px]" : ""}`}>
          <Users className={`${isMobile ? "h-2.5 w-2.5" : "h-4 w-4"} text-muted-foreground`} />
          <Badge 
            variant={spotsLeft === 0 ? "destructive" : "outline"}
            size={isMobile ? "xs" : "default"}
            className={isMobile ? "text-[9px]" : ""}
          >
            {attendeesCount}/{capacity}
          </Badge>
        </div>
      </TableCell>
      <TableCell className={isMobile ? "px-1 py-1" : ""}>
        <StatusBadge status={status} isMobile={isMobile} />
      </TableCell>
    </TableRow>
  );
};
